import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';
import { getMenuIcon } from "../../../../../helpers/theme/getMenuIcon";
import useMenuAction from "../../../../../hooks/menu/useMenuAction";
import useMenuData from "../../../../../hooks/menu/useMenuData";
import useConfig from "../../../../../hooks/theme/useConfig";
import { NAVIGATION } from "../../../../../locales/components/namespaces";
import PermissionHOC from "../../../../../modules/permission/components/permissionHOC/permissionHOC";
import { MenuOrientation } from "../../../../../themes/config";

// project import

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

const NavItem = ({ item, level, onClickAction, isAction = false }) => {
	const {t} = useTranslation(NAVIGATION);
	const theme = useTheme();
	const { menuOrientation } = useConfig();
	const downLG = useMediaQuery(theme.breakpoints.down('lg'));
	const { drawerOpen, openItem } = useMenuData()
	const { setMenuActiveItem, setDrawerOpen, } = useMenuAction()
	const [isHovered, setIsHovered] = useState(false);
	const closeRef = useRef(null)
	let itemTarget = '_self';
	if (item.target) {
		itemTarget = '_blank';
	}
	let listItemProps = {};
	
	if(!isAction) {
		if (item?.external) {
			listItemProps = { component: 'a', href: item.url, target: itemTarget };
		} else {
			listItemProps = {
				component: forwardRef((props, ref) => <Link {...props} to={item.url} target={itemTarget} ref={ref}/>)
			};
		}
	}
	const Icon = getMenuIcon(item.icon)
	const itemIcon = item.icon ? <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }}/> : false;

	const isSelected = openItem.findIndex((id) => id === item.id) > -1;
	const pathname = document.location.pathname;
	const urlParams = document.location.search;
	// active menu item on page load
	useEffect(() => {
		if ((`${pathname}${urlParams}`=== item.url) && !isAction && openItem[0] !== item.id) {
			setMenuActiveItem({ openItem: [item.id] });
		}
		// eslint-disable-next-line
	}, [pathname, urlParams]);

	const textColor = 'primary.contrastText';
	
	
	const checkHover = useCallback((e => {
		if (closeRef.current) {
			const mouseOver = closeRef.current.contains(e.target);
			if (!isHovered && mouseOver) {
				//console.log(isHovered, mouseOver);
				setIsHovered(true)
			}
			
			if (isHovered && !mouseOver) {
				setIsHovered(false)
			}
		}
	}), [isHovered])
	
	useEffect(() => {
		window.addEventListener("mousemove", checkHover, true);
		return () => {
			window.removeEventListener("mousemove", checkHover, true);
		}
	}, [checkHover, isHovered])
	const isNew = useMemo(() => (item?.isNew), [item?.isNew])

	const navItem = (
		<>
			{menuOrientation === MenuOrientation.VERTICAL || downLG ? (
				<ListItemButton
					{...listItemProps}
					ref={closeRef}
					onMouseEnter={(e) => {
						e.preventDefault();
						setIsHovered(true)
					}}
					onMouseLeave={(e) => {
						e.preventDefault();
						setIsHovered(false)
					}}
					disabled={item.disabled}
					selected={isSelected}
					sx={{
						mb: 4,
						zIndex: 1201,
						borderRadius: '12px',
						marginBottom:'16px',
						background: theme.palette.secondary.main,
						...(drawerOpen && {
							display: 'flex',
							padding: '16px 48px 16px 32px',
							gap: '6px',
							maxHeight: '56px',
							minWidth: '240px',
							'&:hover': {
								borderRadius: '12px',
								background: theme.palette.primary.dark,
								boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset',
								h6: {
									color: '#fff'
								}
							},
							'&.Mui-selected': {
								borderRadius: '12px',
								background: theme.palette.primary.dark,
								boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset',
								h6: {
									color: '#fff'
								},
								'&:hover': {
									borderRadius: '12px',
									background: theme.palette.primary.dark,
									boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset',
									
									h6: {
										color: '#fff'
									}
								}
							},
						}),
						...(!drawerOpen && {
							display: 'flex',
							padding: '10px',
							alignItems: 'center',
							justifyContent: 'center',
							// maxWidth: '44px',
							'&:hover': {
								background: theme.palette.primary.dark,
							},
							'&.Mui-selected': {
								background: theme.palette.primary.dark,
								'&:hover': {
									background: theme.palette.primary.dark,
								}
							}
						})
					}}
					onClick={(event) => {
						event.stopPropagation()
						setMenuActiveItem({ openItem: [item.id] })
						if (downLG) setDrawerOpen(false);
						if(isAction) onClickAction()
					}}
					title={item.hoverMessage}
				>
					<ListItemIcon
						sx={{
							minWidth: 24,
							color: '#FFFFFF'
						}}
					>
						{itemIcon}
					</ListItemIcon>
					{(drawerOpen || (!drawerOpen && level !== 1)) && (
						<ListItemText
							primary={
								<Typography
									variant="body1"
									sx={{
										color: isSelected ? textColor : textColor,
										textWrap: 'wrap',
									}}
								>
									{isNew ? <span>{item.title}<sup style={{
										color: '#d32f2f',
										paddingLeft: '4px',
										fontWeight: 'bold'
									}}>{t('NEW')}</sup></span> : item.title}
								</Typography>
							}
						/>
					)}
					{(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
						<Chip
							color={item.chip.color}
							variant={item.chip.variant}
							size={item.chip.size}
							label={item.chip.label}
							avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
						/>
					)}
				</ListItemButton>
			) : (
				<ListItemButton
					{...listItemProps}
					disabled={item.disabled}
					selected={isSelected}
					onMouseEnter={(e) => {
						e.preventDefault();
						setIsHovered(true)
					}}
					onMouseLeave={(e) => {
						e.preventDefault();
						setIsHovered(false)
					}}
					sx={{
						mb: 4,
						zIndex: 1201,
						borderRadius: '12px',
						marginBottom:'16px',
						background: theme.palette.secondary.main,
						...(drawerOpen && {
							display: 'flex',
							padding: '16px 48px 16px 32px',
							gap: '6px',
							minWidth: '240px',
							maxHeight: '56px',
							'&:hover': {
								borderRadius: '12px',
								background: theme.palette.primary.dark,
								boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset',
								h6: {
									color: '#fff'
								}
							},
							'&.Mui-selected': {
								borderRadius: '12px',
								background: theme.palette.primary.dark,
								boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset',
								h6: {
									color: '#fff'
								},
								'&:hover': {
									borderRadius: '12px',
									background: theme.palette.primary.dark,
									boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset',
									
									h6: {
										color: '#fff'
									}
								}
							},
						}),
						...(!drawerOpen && {
							display: 'flex',
							padding: '10px',
							alignItems: 'center',
							justifyContent: 'center',
							maxWidth: '44px',
							'&:hover': {
								background: theme.palette.primary.dark,
							},
							'&.Mui-selected': {
								background: theme.palette.primary.dark,
								'&:hover': {
									background: theme.palette.primary.dark,
								}
							}
						})
					}}
					onClick={(event) => {
						event.stopPropagation()
						setMenuActiveItem({ openItem: [item.id] })
						if (downLG) setDrawerOpen(false);
						if(isAction) onClickAction()
					}}
					title={item.hoverMessage}
				>
					<ListItemIcon
						sx={{
							minWidth: 24,
							color: '#FFFFFF'
						}}
					>
						{itemIcon}
					</ListItemIcon>
					{(drawerOpen || (!drawerOpen && level !== 1)) && (
						<ListItemText
							primary={
								<Typography
									variant="body1"
									sx={{
										color: isSelected ? textColor : textColor,
										textWrap: 'wrap',
									}}
								>
									{item.title}
								</Typography>
							}
						/>
					)}
					{(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
						<Chip
							color={item.chip.color}
							variant={item.chip.variant}
							size={item.chip.size}
							label={item.chip.label}
							avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
						/>
					)}
				</ListItemButton>
			)}
		</>
	)
	
	return item.permissions && item.permissions.length > 0 ? (
		<PermissionHOC
			requiredPermissions={[...item.permissions]}>
			{navItem}
		</PermissionHOC>
	) : (
		<>{navItem}</>
	)
}
//
// NavItem.propTypes = {
//     item: PropTypes.object,
//     level: PropTypes.number
// };

export default NavItem;
